// Settings → Alerts (admins only): workspace-wide error spike thresholds, applied to every site
// and app, and the last error spikes across all of them. Each site can still opt out in Site
// settings → Alerts (alerts.ts). The API refuses everyone but admins (guard.ts).
// Failed events (admins only too): the email switch for events the processor gave up on, a test
// email, and the last few saved batches.
import type { DeadLetterBatchView, ErrorAlertSettingsView, ErrorIncidentView, GlobalAlertsView } from "../src/alerts-types";

interface Opts {
  el: HTMLElement;
  current: () => boolean;
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

async function call<T>(method: string, body?: unknown, path = "/api/alert-settings"): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

const ERRORS: Record<string, string> = {
  not_allowed: "Only MonoLog admins can change these settings.",
  bad_window_hours: "Pick one of the listed time windows.",
  bad_multiplier: "Pick one of the listed multipliers.",
  bad_min_errors: "Minimum errors must be a whole number from 1 to 1,000.",
  bad_recipients: "Pick who gets the emails.",
  email_not_configured: "Email isn't set up on the dashboard (EMAIL_API_KEY).",
  email_failed: "The email provider refused it; see the dashboard's logs.",
};

const fmtWhen = (ms: number) => new Date(ms).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
function fmtSpan(ms: number): string {
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${Math.max(1, m)} min`;
  const h = Math.round(m / 60);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} days`;
}
const fmtN = (n: number) => Math.round(n).toLocaleString("en-US");

function incident(i: ErrorIncidentView): string {
  const span = i.closed_at ? `${fmtWhen(i.opened_at)} · lasted ${fmtSpan(i.closed_at - i.opened_at)}` : `since ${fmtWhen(i.opened_at)}`;
  const nums =
    i.errors !== null
      ? `${fmtN(i.errors)} errors${i.hours ? ` in ${i.hours} h` : ""}${i.usual !== null ? `, usually ${i.usual < 1 ? "almost none" : fmtN(i.usual)}` : ""}`
      : "";
  return `<div class="alr-inc"><span class="pill ${i.closed_at ? "none" : "poor"}">${i.closed_at ? "Resolved" : "Ongoing"}</span><a class="alr-inc-what" href="/project/${encodeURIComponent(i.project)}/errors/">${esc(i.project_name)}</a>${
    nums ? `<span>${nums}</span>` : ""
  }<span class="muted">${span}</span></div>`;
}

function deadBatch(b: DeadLetterBatchView): string {
  const sites = b.sites.length ? b.sites.join(", ") : "unknown sites";
  return `<div class="alr-inc"><span class="pill ${b.replayed_at ? "none" : "poor"}">${b.replayed_at ? "Replayed" : "Saved"}</span><span class="alr-inc-what">${fmtN(b.count)} ${
    b.count === 1 ? "event" : "events"
  }</span><span>${esc(sites)}</span><span class="muted">${fmtWhen(b.received_at)}</span></div>`;
}

function deadCard(g: GlobalAlertsView): string {
  const d = g.dead_letter;
  const list = d.batches.length ? d.batches.map(deadBatch).join("") : `<p class="muted usr-note">None so far.</p>`;
  return `<div class="card">
    <div class="card-head"><h2>Failed events</h2></div>
    <form class="alr-form dlq-form">
      <label class="alr-rule gea-on"><input type="checkbox" name="on"${d.on ? " checked" : ""}><span><b>Failed events alerts</b><span class="muted">Email admins (never site owners) when events fail processing 10 times and are set aside, and again after 24 hours with none. They're saved and can be replayed.</span></span></label>
      <div class="usr-actions"><button class="btn primary" type="submit">Save</button><button class="btn dlq-test" type="button">Send test email</button><span class="acct-msg" role="status"></span></div>
    </form>
    <div class="alr-recent"><h3>Recent failed batches</h3>${list}</div>
  </div>`;
}

export async function renderAlertSettings(o: Opts): Promise<void> {
  o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Global error alerts</h2></div><div class="acct-msg">Loading…</div></div>`;
  try {
    const data = await call<GlobalAlertsView>("GET");
    if (o.current()) draw(o, data);
  } catch (err) {
    if (o.current())
      o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Global error alerts</h2></div><div class="acct-msg error">Couldn't load alert settings (${esc((err as Error).message)}).</div></div>`;
  }
}

function draw(o: Opts, g: GlobalAlertsView): void {
  const s = g.error;
  const opt = (v: number, label: string, cur: number) => `<option value="${v}"${v === cur ? " selected" : ""}>${label}</option>`;
  const [minLo, minHi] = g.options.min_errors;
  const recent = g.incidents.length
    ? `<div class="alr-recent"><h3>Recent error alerts</h3>${g.incidents.map(incident).join("")}</div>`
    : `<div class="alr-recent"><h3>Recent error alerts</h3><p class="muted usr-note">None yet.</p></div>`;

  o.el.innerHTML = `<div class="card">
    <div class="card-head"><h2>Global error alerts</h2></div>
    <form class="alr-form gea-form">
      <label class="alr-rule gea-on"><input type="checkbox" name="on"${s.on ? " checked" : ""}><span><b>Error spike alerts</b><span class="muted">Email when a site or app suddenly has far more errors than usual. Resolved and ignored errors don't count.</span></span></label>
      <div class="gea-grid">
        <label class="fld"><span>Compare the last</span><select class="btn" name="window_hours">${g.options.window_hours
          .map((h) => opt(h, h === 1 ? "1 hour" : `${h} hours`, s.window_hours))
          .join("")}</select></label>
        <label class="fld"><span>Alert at</span><select class="btn" name="multiplier">${g.options.multiplier.map((m) => opt(m, `${m}× the usual errors`, s.multiplier)).join("")}</select></label>
        <label class="fld"><span>And at least</span><span class="gea-min"><input class="acct-input" type="number" name="min_errors" inputmode="numeric" min="${minLo}" max="${minHi}" step="1" value="${s.min_errors}" required><span class="muted">errors</span></span></label>
        <label class="fld"><span>Send to</span><select class="btn" name="recipients">
          <option value="owners"${s.recipients === "owners" ? " selected" : ""}>Site owners and admins</option>
          <option value="admins"${s.recipients === "admins" ? " selected" : ""}>Admins only</option>
        </select></label>
      </div>
      <p class="muted usr-note">"Usual" is the same hours on the previous 4 weeks. An email goes out when a spike starts and again when errors are back to normal. Sites can opt out in Site settings → Alerts.</p>
      <div class="usr-actions"><button class="btn primary" type="submit">Save</button><span class="acct-msg" role="status"></span></div>
    </form>
    ${recent}
  </div>${deadCard(g)}`;

  const form = o.el.querySelector<HTMLFormElement>(".gea-form")!;
  const on = form.querySelector<HTMLInputElement>('input[name="on"]')!;
  const fields = [...form.querySelectorAll<HTMLSelectElement | HTMLInputElement>(".gea-grid select, .gea-grid input")];
  const sync = () => fields.forEach((f) => (f.disabled = !on.checked));
  on.addEventListener("change", sync);
  sync();
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const msg = form.querySelector<HTMLElement>("[role=status]")!;
    const f = new FormData(form);
    const next: Partial<ErrorAlertSettingsView> = { on: on.checked };
    // Disabled fields aren't in the form data: keep what's saved for them.
    if (on.checked) {
      next.window_hours = Number(f.get("window_hours"));
      next.multiplier = Number(f.get("multiplier"));
      next.min_errors = Number(f.get("min_errors"));
      next.recipients = f.get("recipients") === "admins" ? "admins" : "owners";
    }
    try {
      const saved = await call<GlobalAlertsView>("PUT", { error: next });
      if (!o.current()) return;
      draw(o, saved);
      const m = o.el.querySelector<HTMLElement>("[role=status]")!;
      m.textContent = "Saved.";
      m.className = "acct-msg";
    } catch (err) {
      msg.textContent = ERRORS[(err as Error).message] ?? `Couldn't save (${(err as Error).message}).`;
      msg.className = "acct-msg error";
    }
  });

  const dlq = o.el.querySelector<HTMLFormElement>(".dlq-form")!;
  const dlqMsg = dlq.querySelector<HTMLElement>("[role=status]")!;
  const say = (text: string, error = false) => {
    dlqMsg.textContent = text;
    dlqMsg.className = error ? "acct-msg error" : "acct-msg";
  };
  dlq.addEventListener("submit", async (e) => {
    e.preventDefault();
    const dlqOn = dlq.querySelector<HTMLInputElement>('input[name="on"]')!.checked;
    try {
      const saved = await call<GlobalAlertsView>("PUT", { dead_letter: { on: dlqOn } });
      if (!o.current()) return;
      draw(o, saved);
      const m = o.el.querySelector<HTMLElement>(".dlq-form [role=status]")!;
      m.textContent = "Saved.";
      m.className = "acct-msg";
    } catch (err) {
      say(ERRORS[(err as Error).message] ?? `Couldn't save (${(err as Error).message}).`, true);
    }
  });
  const testBtn = dlq.querySelector<HTMLButtonElement>(".dlq-test")!;
  testBtn.addEventListener("click", async () => {
    testBtn.disabled = true;
    say("Sending…");
    try {
      const r = await call<{ sent_to: string }>("POST", {}, "/api/alert-settings/test-dlq");
      say(`Test email sent to ${r.sent_to}.`);
    } catch (err) {
      say(ERRORS[(err as Error).message] ?? `Couldn't send (${(err as Error).message}).`, true);
    } finally {
      testBtn.disabled = false;
    }
  });
}
